import React, { useState } from "react";
import { Box, UnstyledButton } from "@mantine/core";
import { IconCalendar, IconPlus } from "@tabler/icons-react";
import FilterChip, { summarizeSelection } from "../../common/FilterChip";
import {
  BaseViewConfig,
  ReportFacetOption,
  ReportV2Definition,
} from "../../../types/reportsV2";
import { dateRangeLabel } from "../../../utils/dateRangeFormat";
import { useFacetOptions } from "../v2/hooks/useFacetOptions";
import { BaseViewPatch, buildToolbarFilters } from "../v2/model/toolbarFilters";
import { ResolvedRange, resolvedHint, sameRange } from "../v2/ReportDateChip";
import MobileAddFilterSheet from "./sheets/MobileAddFilterSheet";
import MobileDateSheet from "./sheets/MobileDateSheet";
import MobileFilterSheet from "./sheets/MobileFilterSheet";
import canvasStyles from "../v2/ReportCanvas.module.css";
import styles from "./MobileReportCanvas.module.css";

export interface MobileReportToolbarProps {
  definition: ReportV2Definition;
  view: BaseViewConfig;
  /** Filter options from the latest run. */
  facets?: Record<string, ReportFacetOption[]>;
  /** Resolved dates of the latest run (date chip hint). */
  resolvedRange?: ResolvedRange | null;
  /** Hidden filters (e.g. staff for an own-scoped Earnings Summary). */
  hiddenFilters?: string[];
  onUpdate: (patch: BaseViewPatch) => void;
}

type OpenSheet =
  | { kind: "date" }
  | { kind: "add" }
  | { kind: "filter"; key: string }
  | null;

/**
 * The phone chip row (scrolls sideways): date range, a chip per shown
 * filter and "+ Filter". Each opens a bottom sheet; the chips and edits are
 * the desktop toolbar's (buildToolbarFilters, useFacetOptions).
 */
export default function MobileReportToolbar({
  definition,
  view,
  facets,
  resolvedRange,
  hiddenFilters,
  onUpdate,
}: MobileReportToolbarProps) {
  const optionsFor = useFacetOptions(facets);
  const filters = buildToolbarFilters(
    definition,
    view,
    onUpdate,
    hiddenFilters,
  );
  const [sheet, setSheet] = useState<OpenSheet>(null);
  const close = () => setSheet(null);

  const { range } = view;
  const rangeLabel = dateRangeLabel(
    range.preset,
    range.start ?? null,
    range.end ?? null,
  );
  const selectedOf = (key: string) => view.filters[key] ?? [];
  const openFilter =
    sheet?.kind === "filter"
      ? (definition.filters.find((f) => f.key === sheet.key) ?? null)
      : null;

  return (
    <>
      <Box className={styles.chipRow} role="toolbar" aria-label="Report view">
        <FilterChip
          variant="outline"
          icon={<IconCalendar size={15} aria-hidden />}
          value={rangeLabel}
          hint={resolvedHint(range, resolvedRange)}
          active={!sameRange(range, definition.default_view.range)}
          aria-label={`Date range: ${rangeLabel}`}
          onClick={() => setSheet({ kind: "date" })}
        />
        {filters.shown.map((f) => {
          const selected = selectedOf(f.key);
          const summary = summarizeSelection(
            optionsFor(f.key, selected),
            selected,
            "All",
          );
          return (
            <FilterChip
              key={f.key}
              variant="outline"
              label={f.label}
              value={summary}
              valueMaxWidth="var(--chip-value-max-width)"
              active={selected.length > 0}
              aria-label={`${f.label} filter: ${summary}`}
              onClick={() => setSheet({ kind: "filter", key: f.key })}
            />
          );
        })}
        {filters.addable.length > 0 && (
          <UnstyledButton
            type="button"
            className={canvasStyles.addFilter}
            onClick={() => setSheet({ kind: "add" })}
          >
            <IconPlus size={15} aria-hidden />
            Filter
          </UnstyledButton>
        )}
      </Box>
      <MobileDateSheet
        opened={sheet?.kind === "date"}
        onClose={close}
        range={range}
        presets={definition.date_range_presets}
        resolved={resolvedRange}
        onChange={(next) => onUpdate({ range: next })}
      />
      <MobileAddFilterSheet
        opened={sheet?.kind === "add"}
        onClose={close}
        filters={filters.addable}
        onPick={(key) => {
          filters.showFilter(key);
          setSheet({ kind: "filter", key });
        }}
      />
      <MobileFilterSheet
        filter={openFilter}
        options={
          openFilter
            ? optionsFor(openFilter.key, selectedOf(openFilter.key))
            : []
        }
        selected={openFilter ? selectedOf(openFilter.key) : []}
        onChange={(values) => {
          if (openFilter) filters.setFilter(openFilter.key, values);
        }}
        onRemove={
          openFilter && !openFilter.always_shown
            ? () => filters.hideFilter(openFilter.key)
            : undefined
        }
        onClose={close}
      />
    </>
  );
}
